// @ts-check

import { api } from '@/api'
import { useCompanyStore } from '@/store/company'
import invariant from 'invariant'

export const getSetupIntent = async () => api.post('cards/intent')

export const updateCardDetails = async ({ paymentMethodId }) => {
  const res = await api.post('cards', { payment_method_id: paymentMethodId })
  if (res.data) return res.data
  return res
}

/**
 * Returns cards of the authenticated user.
 */
export const getCards = async () => api.get('cards')

/**
 * Returns unpaid company invoices of the authenticated company.
 * @returns {Promise<CompanyInvoiceResource[]>}
 */
export const getUnpaidCompanyInvoices = async () => {
  const { company } = useCompanyStore()

  invariant(company?.id, 'Company id is required')

  const { data: unpaidCompanyInvoices } = await api.get(
    `company/${company.id}/invoices/unpaid`
  )

  return unpaidCompanyInvoices
}

/**
 * Activate or continue yearly plan
 * @returns {Promise<YearlyPlanResource[]>}
 */
export const activateYearlyPlan = async (planId) => {
  const res = await api.post(
    'subscriptions/core-subscription/activate-annual-plan',
    {
      subscription_plan_id: planId
    }
  )
  return res.data
}

/**
 * Pays company invoice with the given id of the given company id.
 * @param {number} invoiceId
 */
export const payCompanyInvoice = async (invoiceId) => {
  const { company } = useCompanyStore()
  invariant(company, 'Company is required')
  return api.post(`company/${company.id}/invoices/${invoiceId}/pay`)
}

/**
 * @returns {Promise<AddonResource[]>}
 */
export const getAvailableAddons = async () => {
  const { data: addons } = await api.get('subscriptions/addons/available')
  return addons
}

/**
 * @param {AddonResource['feature']} feature
 * @returns {Promise<AddonResource>}
 */
export const getAddon = async (feature) => {
  const { data: addon } = await api.get(`subscriptions/addons/${feature}`)
  return addon
}

/**
 * * OLD
 * @returns {Promise<AvailablePlansResource>}
 */
export const getAvailablePlans = async () =>
  api.get('subscriptions/plans/available')

/**
 * @returns {Promise<CorePeriodsResource>}
 */
export const getCurrentAndUpcomingPeriods = async () => {
  const { data: periods } = await api.get(
    'subscriptions/core-subscription/periods'
  )

  if (!periods.upcoming_subscription_period) {
    periods.upcoming_subscription_period = {
      period_start: null,
      period_end: null
    }
  }

  return periods
}

/**
 * @returns {Promise<CoreUpcomingPlanResource|[]>}
 */
export const getUpcomingPlan = async () => {
  const { data: upComingPlan } = await api.get(
    'subscriptions/core-subscription/upcoming-plan'
  )

  return upComingPlan
}

/**
 * @returns {Promise<CorePlanResource>}
 */
export const getCurrentPlan = async () => {
  const { data: currentPlan } = await api.get(
    'subscriptions/core-subscription/current-plan'
  )
  return currentPlan
}

/**
 * @returns {Promise<CoreAnnualAvailablePlansResource>}
 */
export const getAvailableAnnualPlans = async () => {
  const { data: availableAnnualPlans } = await api.get(
    'subscriptions/core-subscription/available-annual-plans'
  )
  return availableAnnualPlans
}

/**
 * @param {number} planId
 * @param {boolean} enabled
 * @returns {Promise<AddonResource>}
 */
export const updateAddon = async (planId, enabled) => {
  const res = await api.post('subscriptions/addons/update', {
    subscription_plan_id: planId,
    enabled
  })
  return res.data
}

/**
 * @param {Parameters<typeof updateAddon>[0]} planId
 */
export const enableAddon = (planId) => updateAddon(planId, true)

/**
 * @param {Parameters<typeof updateAddon>[0]} planId
 */
export const disableAddon = (planId) => updateAddon(planId, false)
